import { observer } from 'mobx-react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import React, { useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useTranslation } from 'react-i18next';
import Keys from '../../../Translations/generated/session-time-out-modal.en.json.keys';
import { useAuth, useWindow } from '@ibe/components';
import useMMBLoginService from '../../../Hooks/useMMBLoginService';

interface Props {
  sessionTimeOut: number;
  sessionTimeOutNotificationOffset: number;
  allowUnauthorizedUser?: boolean;
  onLogoutRedirection: () => void;
}

const SessionTimeOutModal = observer((props: Props) => {
  const window = useWindow();
  const { t } = useTranslation('session-time-out-modal');
  const loginService = useMMBLoginService();
  const auth = useAuth();
  const {
    sessionTimeOut,
    sessionTimeOutNotificationOffset,
    onLogoutRedirection,
    allowUnauthorizedUser
  } = props;
  const [sessionTimeOutModalOpen, setSessionTimeOutModalOpen] = useState(false);
  const [sessionTimeOutNotification, setSessionTimeOutNotification] = useState(false);
  const [refreshInProgress, setRefreshInProgress] = useState(false);

  const isLoggedIn = () => {
    return auth.authService.isLoggedIn || auth.authState.isAgency; //NOSONAR
  };

  const onPrompt = (): void => {
    if (isLoggedIn() || allowUnauthorizedUser) {
      setSessionTimeOutNotification(true);
    }
  };

  const onIdle = (): void => {
    if (isLoggedIn() || allowUnauthorizedUser) {
      setSessionTimeOutNotification(false);
      setSessionTimeOutModalOpen(true);
    }
  };

  const onActive = (): void => {
    if (isLoggedIn() || allowUnauthorizedUser) {
      setSessionTimeOutNotification(false);
    }
  };

  const { activate } = useIdleTimer({
    onIdle,
    onPrompt,
    onActive,
    timeout: sessionTimeOut,
    promptBeforeIdle: sessionTimeOutNotificationOffset,
    eventsThrottle: 200,
    events: ['keydown', 'mousedown', 'touchstart']
  });

  const handleRefreshSession = (): void => {
    if (refreshInProgress) return;

    setRefreshInProgress(true);

    loginService
      .refreshToken()
      .then(() => {
        auth.persistToken();
        activate();
        setSessionTimeOutNotification(false);
        setSessionTimeOutModalOpen(false);
      })
      .catch(() => {
        toggleSessionExpiredModal();
      })
      .finally(() => {
        setRefreshInProgress(false);
      });
  };

  const toggleSessionExpiredModal = (): void => {
    if (sessionTimeOutNotification) {
      setSessionTimeOutNotification(false);
    }
    setSessionTimeOutModalOpen(false);
    onLogoutRedirection();
  };
  const toggleSessionExpirationNotificationModal = (): void => {
    setSessionTimeOutNotification(false);
  };

  return (
    <>
      <Modal
        isOpen={sessionTimeOutNotification}
        toggle={toggleSessionExpirationNotificationModal}
        container={window?.document?.getElementById('iso') || undefined}
        backdrop="static"
        keyboard={false}
      >
        <ModalHeader
          close={
            <button
              className="close"
              onClick={toggleSessionExpirationNotificationModal}
              type="button"
              aria-label={t(Keys.close)}
            >
              <span aria-hidden="true">×</span>
            </button>
          }
        >
          {t(Keys.titleIsAboutToExpire)}
        </ModalHeader>
        <ModalBody>
          <div>{t(Keys.sessionIsAboutToExpire)}</div>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleRefreshSession} disabled={refreshInProgress}>
            {t(Keys.buttonRefresh)}
          </Button>
        </ModalFooter>
      </Modal>
      <Modal
        isOpen={sessionTimeOutModalOpen}
        toggle={toggleSessionExpiredModal}
        container={window?.document?.getElementById('iso') || undefined}
        keyboard={false}
      >
        <ModalHeader
          close={
            <button
              className="close py-1 px-1"
              onClick={toggleSessionExpiredModal}
              type="button"
              aria-label={t(Keys.close)}
            >
              <span aria-hidden="true">×</span>
            </button>
          }
        >
          {t(Keys.titleExpired)}
        </ModalHeader>
        <ModalBody>
          <div>{t(Keys.sessionExpiredText)}</div>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={toggleSessionExpiredModal}>
            {t(Keys.buttonExpired)}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
});

export default SessionTimeOutModal;
